import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import LongevityScore from "../../components/LongevityScore";
import LoadingAnimation from "./LoadingAnimation";
import useApiWithRetry from "../../hooks/useApiWithRetry";
import styles from "./Result.module.sass";

const Result = () => {
    const { email } = useParams();
    const [showContent, setShowContent] = useState(false);
    const [loadingStartTime, setLoadingStartTime] = useState(Date.now());
    const [displayScoreData, setDisplayScoreData] = useState(null);

    // We need a separate loading state to handle the minimum loading time
    const [loading, setLoading] = useState(true);

    // Constants for loading time
    const MINIMUM_LOADING_TIME = 5000; // 5 seconds minimum

    // Set loading start time only once when component mounts
    useEffect(() => {
        setLoadingStartTime(Date.now());
    }, []);

    // Define callbacks for the API hook
    const handleSuccess = useCallback((data) => {
        console.log("Data received from server:", data);
        setDisplayScoreData(data);

        // Calculate how long we've been loading
        const currentTime = Date.now();
        const elapsedTime = currentTime - loadingStartTime;

        if (elapsedTime >= MINIMUM_LOADING_TIME) {
            // If we've already been loading for 5+ seconds, stop loading immediately
            setLoading(false);
        } else {
            // Otherwise, wait until we reach the minimum loading time
            const remainingTime = MINIMUM_LOADING_TIME - elapsedTime;
            setTimeout(() => {
                setLoading(false);
            }, remainingTime);
        }
    }, [loadingStartTime]);

    const handleError = useCallback(() => {
        // onError callback
        setLoading(false);
    }, []);

    // Use our custom hook for API requests with retry
    useApiWithRetry(
        `survey/responses/${email}/`,
        {},
        {
            maxRetries: 12,
            retryDelay: 5000
        },
        handleSuccess,
        handleError
    );

    // Flag to track if this is the first render
    const isFirstRender = useRef(true);

    // Control when the API call is made
    useEffect(() => {
        // Only allow the API call on the first render
        if (!isFirstRender.current) {
            console.log("Preventing additional API calls");
            return;
        }

        isFirstRender.current = false;
        console.log("Initial API call allowed");

        // No need to do anything else here, the hook will make the call
    }, [email]);

    const handleAnimationComplete = () => {
        setShowContent(true);
    };

    // Default values for LongevityScore if data is not available
    const defaultScoreData = {
        score_percentage: 99,
        score_label: "Health Conscious",
        score_description: [
            "Like the panther, you move with strength, agility, and purpose.",
            "Your dedication to health shows that you are intentional about your well-being,",
            "which will set you apart in the long run.",
            "However, even the strongest animals need the right environment to thrive—small",
            "refinements can help you unlock even greater vitality and resilience."
        ],
        avatar: "🐅",
        animal_name: "The Tiger",
        animal_description: "The Tiger – Powerful, resilient, and agile, maintaining strength through consistent training.",
        animal_image: "https://api.zovalife.co.uk/static/animals/tiger.svg",
    };

    // Use the display data or default if not available
    const dataToDisplay = displayScoreData || defaultScoreData;

    return (
        <div className={styles.resultContainer}>
            {/* Always render the LongevityScore component, it will be visible after loading */}
            <div className={`${styles.resultContent} ${showContent ? styles.visible : styles.hidden}`}>
                <LongevityScore
                    key={displayScoreData ? 'server-data' : 'default-data'} // Force re-render when data source changes
                    score={Math.round(dataToDisplay.score_percentage)}
                    label={dataToDisplay.score_label}
                    animalName={dataToDisplay.animal_name}
                    animalImage={dataToDisplay.animal_image}
                    description={dataToDisplay.score_description}
                />
            </div>

            {/* Loading animation overlay */}
            <LoadingAnimation
                isLoading={loading}
                onAnimationComplete={handleAnimationComplete}
            />
        </div>
    );
};

export default Result;
